<template>
  <div id="closet-wardrobe">
    <div class="page-header">
      <div class="title">나의 옷장</div>
      <div class="subtitle">브랜드·모델별 사이즈와 핏을 기록해 다음 구매 때 바로 참고하세요</div>
    </div>

    <!-- 상단: 카테고리 필터 탭 + 등록 버튼 -->
    <div class="wardrobe-header">
      <div class="category-tabs">
        <button
          v-for="tab in categoryTabs"
          :key="tab.value"
          class="tab-btn"
          :class="{ active: search.category === tab.value }"
          @click="selectCategory(tab.value)"
        >{{ tab.label }}</button>
      </div>
      <button class="btn-register" @click="goToRegister">+ 등록</button>
    </div>

    <!-- 검색 -->
    <div class="search-bar">
      <input
        type="text"
        v-model="search.keyword"
        placeholder="브랜드명, 소분류 검색"
        @keyup.enter="fetchClothingList"
      />
      <button class="btn-search" @click="fetchClothingList">검색</button>
      <button v-if="hasSearchFilter" class="btn-reset" @click="resetSearch">초기화</button>
    </div>

    <!-- 본문: 좌측 카드 목록(2열) + 우측 추천 패널(1열) -->
    <div class="wardrobe-body">
      <!-- 좌측: 의류 카드 리스트 -->
      <div class="list-section">
        <div class="list-info" v-if="clothingList.length > 0">
          <span class="list-count">{{ clothingList.length }}건</span>
        </div>

        <div class="clothing-card-list" v-if="clothingList.length > 0">
          <div
            class="clothing-card"
            v-for="item in clothingList"
            :key="item.id"
            @click="goToDetail(item.id)"
          >
            <div class="cc-top">
              <span class="badge category sm">{{ getCategoryLabel(item.category) }}</span>
              <span class="cc-brand">{{ item.brandName || '브랜드 미입력' }}</span>
              <span class="cc-stars" v-if="item.satisfaction">
                <span v-for="s in 5" :key="s" class="star-sm" :class="{ active: s <= item.satisfaction }">&#9733;</span>
              </span>
            </div>
            <div class="cc-product" v-if="item.productName || item.subCategory">
              {{ item.productName || item.subCategory }}
            </div>
            <div class="cc-sizerow">
              <span class="cc-size" v-if="item.labeledSize">{{ item.labeledSize }}</span>
              <span v-if="item.fitFeedback" class="fit-chip" :class="fitClass(item.fitFeedback)">
                {{ getFitFeedbackLabel(item.fitFeedback) }}
              </span>
              <span class="cc-nosize" v-if="!item.labeledSize && !item.fitFeedback">사이즈·핏 미기록</span>
            </div>
            <div class="card-measures" v-if="item.measurements && Object.keys(item.measurements).length > 0">
              <span v-for="(val, key) in item.measurements" :key="key" class="measure-tag">
                {{ getMeasureLabel(item.category, String(key)) }} {{ formatMeasure(Number(val), getUnit(item.category, String(key)), toInch) }}
              </span>
            </div>
          </div>
        </div>

        <div v-else class="empty-message">
          <div class="empty-icon">&#128090;</div>
          <p>등록된 의류가 없습니다</p>
          <button class="btn-primary" @click="goToRegister">첫 의류 등록하기</button>
        </div>
      </div>

      <!-- 우측: 내게 맞는 사이즈 추천 -->
      <aside class="fit-reco-panel">
        <div class="reco-head">
          <div class="reco-title">
            <span class="reco-icon">&#128207;</span> 내게 맞는 사이즈
          </div>
          <button class="unit-toggle" @click="toggleUnit">
            <span :class="{ on: !toInch }">cm</span>
            <span class="sep">/</span>
            <span :class="{ on: toInch }">in</span>
          </button>
        </div>

        <div class="reco-cat-label">{{ getCategoryLabel(recoCategory) }}</div>

        <template v-if="hasRecoData">
          <!-- 공통 부위 (소분류 무관) -->
          <div class="reco-rows" v-if="fitReco.common.length">
            <div class="reco-section-label">공통 부위</div>
            <div
              class="reco-row"
              v-for="row in fitReco.common"
              :key="row.key"
              :class="{ empty: row.recommended == null }"
            >
              <span class="reco-part">{{ row.label }}</span>
              <span class="reco-val" v-if="row.recommended != null">
                {{ convertMeasure(row.recommended, row.unit, toInch) }}<em>{{ displayUnit(row.unit, toInch) }}</em>
              </span>
              <span class="reco-val muted" v-else>–</span>
              <span class="reco-basis">{{ recoDetail(row) }}</span>
            </div>
          </div>

          <!-- 소분류별로 달라지는 길이 부위 -->
          <div class="reco-rows length" v-for="g in fitReco.lengthGroups" :key="g.key">
            <div class="reco-section-label">{{ g.label }} <span class="sub">소분류별</span></div>
            <div class="reco-row" v-for="it in g.items" :key="it.subCategory">
              <span class="reco-part">{{ it.subCategory }}</span>
              <span class="reco-val">
                {{ convertMeasure(it.row.recommended, g.unit, toInch) }}<em>{{ displayUnit(g.unit, toInch) }}</em>
              </span>
              <span class="reco-basis">{{ recoDetail(it.row) }}</span>
            </div>
          </div>
        </template>

        <div class="reco-empty" v-else>
          <p>핏 평가가 입력된 옷이 아직 없어요.</p>
          <p class="hint">옷을 등록할 때 <b>핏 평가</b>(작음/딱맞음/큼)를 함께 남기면<br />부위별로 내게 맞는 수치를 계산해 드려요.</p>
        </div>

        <div class="reco-foot" v-if="hasRecoData">
          같은 카테고리 옷들의 실측치와 핏 평가를 비교해 추정한 값이에요.
        </div>
      </aside>
    </div>

    <!-- 브랜드별 사이즈 요약 -->
    <div class="summary-section" v-if="brandSummary.length > 0">
      <div class="summary-title">브랜드별 사이즈 요약</div>
      <div class="summary-list">
        <div v-for="(items, brand) in groupedBrandSummary" :key="brand" class="summary-brand">
          <div class="brand-name">{{ brand }} <span class="brand-count">({{ getTotalCount(items) }}건)</span></div>
          <div class="brand-items">
            <div v-for="item in items" :key="item.category + item.labeledSize" class="brand-item">
              <span class="badge category sm">{{ getCategoryLabel(item.category) }}</span>
              <span class="item-size">{{ item.labeledSize }}</span>
              <span class="item-stars" v-if="item.avgSatisfaction">
                <span v-for="s in 5" :key="s" class="star-sm" :class="{ active: s <= Math.round(item.avgSatisfaction) }">&#9733;</span>
              </span>
              <span v-if="item.fitFeedback" class="fit-chip sm" :class="fitClass(item.fitFeedback)">{{ getFitFeedbackLabel(item.fitFeedback) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  ClosetClothing,
  CLOTHING_CATEGORY_OPTIONS,
  getCategoryLabel,
  getFitFeedbackLabel,
  getMeasureLabel,
  MEASUREMENT_FIELDS,
  computeFitReco,
  convertMeasure,
  displayUnit,
  formatMeasure,
  UNIT_STORAGE_KEY,
  type FitRecoRow,
  type FitRecoResult,
} from "../class";
import * as closetApi from "../api";

export default defineComponent({
  name: "ClosetWardrobeView",

  setup() {
    const router = useRouter();
    const route = useRoute();
    const clothingList = ref<ClosetClothing[]>([]);
    const allClothing = ref<ClosetClothing[]>([]); // 추천 계산용(필터 무관 전체)
    const brandSummary = ref<any[]>([]);

    // 인치/cm 토글 (localStorage 유지)
    const toInch = ref<boolean>(localStorage.getItem(UNIT_STORAGE_KEY) === "in");
    const toggleUnit = () => {
      toInch.value = !toInch.value;
      localStorage.setItem(UNIT_STORAGE_KEY, toInch.value ? "in" : "cm");
    };

    const categoryTabs = [
      { value: "", label: "전체" },
      ...CLOTHING_CATEGORY_OPTIONS,
    ];

    const search = reactive({
      category: (route.query.category as string) || "",
      keyword: "",
    });

    const hasSearchFilter = computed(() => search.category !== "" || search.keyword !== "");

    const groupedBrandSummary = computed(() => {
      const grouped: Record<string, any[]> = {};
      brandSummary.value.forEach((item: any) => {
        if (!grouped[item.brandName]) grouped[item.brandName] = [];
        grouped[item.brandName].push(item);
      });
      return grouped;
    });

    const getTotalCount = (items: any[]): number => {
      return items.reduce((sum: number, item: any) => sum + (item.count || 0), 0);
    };

    // ===== 추천 계산 =====
    // 전체 선택 시엔 옷이 가장 많은 카테고리를 기본으로 보여준다.
    const mostCommonCategory = computed(() => {
      const counts: Record<string, number> = {};
      allClothing.value.forEach((c: any) => {
        counts[c.category] = (counts[c.category] || 0) + 1;
      });
      let best = "TOP";
      let max = -1;
      Object.entries(counts).forEach(([cat, n]) => {
        if (n > max) { max = n; best = cat; }
      });
      return best;
    });

    const recoCategory = computed(() => search.category || mostCommonCategory.value);

    const fitReco = computed<FitRecoResult>(() =>
      computeFitReco(recoCategory.value, allClothing.value)
    );

    const hasRecoData = computed(
      () =>
        fitReco.value.common.some((r) => r.recommended != null) ||
        fitReco.value.lengthGroups.length > 0
    );

    const getUnit = (category: string, key: string): string => {
      const fields = MEASUREMENT_FIELDS[category];
      return fields?.find((f) => f.key === key)?.unit ?? "cm";
    };

    // 핏 색상 등급: 작음=파랑(업), 딱맞음=초록(재구매 OK), 큼=앰버(다운)
    const fitClass = (v: string): string => {
      return (
        {
          VERY_TIGHT: "fit-t2",
          TIGHT: "fit-t1",
          PERFECT: "fit-ok",
          LOOSE: "fit-l1",
          VERY_LOOSE: "fit-l2",
        } as Record<string, string>
      )[v] || "";
    };

    // 추천 근거/범위 문구
    const recoDetail = (row: FitRecoRow): string => {
      const u = displayUnit(row.unit, toInch.value);
      const cv = (v: number | null) =>
        v == null ? "" : convertMeasure(v, row.unit, toInch.value);
      let rangePart = "";
      if (row.boundType === "range" && row.low != null && row.high != null) {
        rangePart = `${cv(row.low)}~${cv(row.high)}${u} · `;
      } else if (row.boundType === "atLeast" && row.low != null) {
        rangePart = `${cv(row.low)}${u} 이상 · `;
      } else if (row.boundType === "atMost" && row.high != null) {
        rangePart = `${cv(row.high)}${u} 이하 · `;
      }
      return rangePart + row.basis;
    };

    const selectCategory = (value: string) => {
      search.category = value;
      // 뒤로가기/새로고침 시 필터 유지되도록 URL query 동기화
      router.replace({ query: value ? { category: value } : {} });
      fetchClothingList();
    };

    const buildSearchParams = () => {
      const params: any = {};
      if (search.category) params.category = search.category;
      if (search.keyword) {
        params.brandName = search.keyword;
        params.subCategory = search.keyword;
      }
      return Object.keys(params).length > 0 ? params : null;
    };

    const fetchClothingList = async () => {
      const data = await closetApi.getClothingList(buildSearchParams());
      clothingList.value = Array.isArray(data) ? data : [];
    };

    const fetchAllClothing = async () => {
      const data = await closetApi.getClothingList(null);
      allClothing.value = Array.isArray(data) ? data : [];
    };

    const fetchBrandSummary = async () => {
      const data = await closetApi.getBrandSummary();
      brandSummary.value = Array.isArray(data) ? data : [];
    };

    const resetSearch = () => {
      search.category = "";
      search.keyword = "";
      router.replace({ query: {} });
      fetchClothingList();
    };

    const goToRegister = () => {
      // 현재 카테고리를 등록 폼에 프리셋 + 복귀 시 필터 유지
      router.push({
        name: "closet-clothing-register",
        query: search.category ? { category: search.category } : {},
      });
    };

    const goToDetail = (id: number) => {
      router.push({
        name: "closet-clothing-detail",
        params: { id },
        query: search.category ? { category: search.category } : {},
      });
    };

    onMounted(async () => {
      await Promise.all([fetchClothingList(), fetchAllClothing(), fetchBrandSummary()]);
    });

    return {
      clothingList,
      brandSummary,
      groupedBrandSummary,
      categoryTabs,
      search,
      hasSearchFilter,
      getTotalCount,
      selectCategory,
      fetchClothingList,
      resetSearch,
      goToRegister,
      goToDetail,
      getCategoryLabel,
      getFitFeedbackLabel,
      getMeasureLabel,
      fitClass,
      Math,
      // 추천/단위
      toInch,
      toggleUnit,
      recoCategory,
      fitReco,
      hasRecoData,
      recoDetail,
      getUnit,
      convertMeasure,
      displayUnit,
      formatMeasure,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/closet-wardrobe.scss";
</style>
